import { PriorityHigh as PriorityHighIcon, ViewList as ViewListIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  IconButton,
} from '@mui/material';
import {
  infrastructureType,
  InfrastructureType,
  stringifyPowerType,
} from '@nohara-dxdev/mt-common';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { useInfrastructureItemMapping } from '@/features/items/hooks/use-infrastructure-item-mapping';
import { formatItemBuilding } from '@/features/items/utils/format-item-building';

import { useInfrastructureOfSubItems } from '../../api';
import { ManageSubItemsDialog } from '../manage-sub-items-dialog';

import { ItemFormData } from './types';

interface InfrastructureOfSubItemListProps {
  infrastructureType: InfrastructureType;
}

export const InfrastructureOfSubItemList = ({
  infrastructureType: currentInfrastructureType,
}: InfrastructureOfSubItemListProps) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<ItemFormData>();
  const [manageSubItemsInfrastructure, setManageSubItemsInfrastructure] = useState<{
    parentItemId: string;
    parentItemNumber: string;
    parentItemName: string;
    childItemId: string;
    childItemNumber: string;
    childItemName: string;
  } | null>(null);

  const currentItemId = getValues('id');

  // Get infrastructures where this item is a sub item
  const { data: infrastructures = [], isLoading: isLoadingInfrastructures } =
    useInfrastructureOfSubItems(currentItemId);

  // Filter infrastructures by current infrastructure type
  const currentTabInfrastructures = useMemo(() => {
    return infrastructures.filter(
      (infra) => infra.infrastructureType === currentInfrastructureType,
    );
  }, [infrastructures, currentInfrastructureType]);

  // Get unique parent and child item IDs for fetching
  const relatedItemIds = useMemo(() => {
    const parentIds = currentTabInfrastructures.map((infra) => infra.parentItemId);
    const childIds = currentTabInfrastructures.map((infra) => infra.childItemId);
    return [...new Set([...parentIds, ...childIds])].filter((id) => id !== currentItemId);
  }, [currentTabInfrastructures, currentItemId]);

  // Fetch related items (both parent and child)
  const { getItem, isLoading: isLoadingItems } = useInfrastructureItemMapping({
    itemIds: relatedItemIds,
    enabled: relatedItemIds.length > 0,
    fetchCircuits: currentInfrastructureType === infrastructureType.electricity,
  });

  return (
    <Box>
      {/* Items Table - Shows BOTH parent and child in infrastructure relationship */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={3}
                align="center"
                sx={{ fontWeight: 'bold', bgcolor: 'grey.50', borderRight: '1px solid #ddd' }}
              >
                {t('infrastructure.parent', 'Parent')}
              </TableCell>
              <TableCell
                colSpan={3}
                align="center"
                sx={{ fontWeight: 'bold', bgcolor: 'grey.50', borderRight: '1px solid #ddd' }}
              >
                {t('infrastructure.child', 'Child')}
              </TableCell>
              {currentInfrastructureType === infrastructureType.electricity && (
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{ fontWeight: 'bold', bgcolor: 'grey.50', borderRight: '1px solid #ddd' }}
                >
                  {t('itemTemplates.detail.circuit', 'Circuit')}
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>
                {t('common.actions', 'Actions')}
              </TableCell>
            </TableRow>
            <TableRow>
              {/* Parent columns */}
              <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                {t('items.list.columns.itemNumber')}
              </TableCell>
              <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                {t('items.list.columns.name')}
              </TableCell>
              <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                {t('reports.loadList.columns.room', 'Room')}
              </TableCell>
              {/* Child columns */}
              <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                {t('items.list.columns.itemNumber')}
              </TableCell>
              <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                {t('items.list.columns.name')}
              </TableCell>
              <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                {t('reports.loadList.columns.room', 'Room')}
              </TableCell>
              {/* Circuit columns (electricity only) */}
              {currentInfrastructureType === infrastructureType.electricity && (
                <>
                  <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                    {t('itemTemplates.detail.childCircuit')}
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                    {t('itemTemplates.detail.typeOfPower')}
                  </TableCell>
                </>
              )}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingInfrastructures ? (
              <TableRow>
                <TableCell
                  colSpan={currentInfrastructureType === infrastructureType.electricity ? 9 : 7}
                  align="center"
                >
                  <Skeleton width="100%" height={40} />
                </TableCell>
              </TableRow>
            ) : currentTabInfrastructures.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={currentInfrastructureType === infrastructureType.electricity ? 9 : 7}
                  align="center"
                >
                  <Typography variant="body2" color="textSecondary">
                    {t(
                      'infrastructure.noSubItems',
                      'This item is not attached as a sub item to any infrastructure',
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              currentTabInfrastructures.map((infrastructure, index) => {
                // Get BOTH parent and child items
                const parentItem = getItem(infrastructure.parentItemId);
                const childItem = getItem(infrastructure.childItemId);
                const isParentLoading = isLoadingItems && !parentItem;
                const isChildLoading = isLoadingItems && !childItem;

                // Build circuit info from child item (circuits belong to the item)
                const circuitInfo = childItem?.circuits?.find(
                  (circuit) => circuit.id === infrastructure.circuitId,
                );

                return (
                  <TableRow
                    key={`${infrastructure.parentItemId}-${infrastructure.childItemId}-${index}`}
                  >
                    {/* Parent Item Info */}
                    <TableCell>
                      {isParentLoading ? <Skeleton width={80} /> : (parentItem?.itemNumber ?? '-')}
                    </TableCell>
                    <TableCell>
                      {isParentLoading ? <Skeleton width={200} /> : (parentItem?.name ?? '-')}
                    </TableCell>
                    <TableCell>
                      {isParentLoading ? (
                        <Skeleton width={150} />
                      ) : (
                        formatItemBuilding(
                          parentItem?.building?.name,
                          parentItem?.floor?.name,
                          parentItem?.locationEntity?.name,
                        ) || '-'
                      )}
                    </TableCell>

                    {/* Child Item Info */}
                    <TableCell>
                      {isChildLoading ? <Skeleton width={80} /> : (childItem?.itemNumber ?? '-')}
                    </TableCell>
                    <TableCell>
                      {isChildLoading ? <Skeleton width={200} /> : (childItem?.name ?? '-')}
                    </TableCell>
                    <TableCell>
                      {isChildLoading ? (
                        <Skeleton width={150} />
                      ) : (
                        formatItemBuilding(
                          childItem?.building?.name,
                          childItem?.floor?.name,
                          childItem?.locationEntity?.name,
                        ) || '-'
                      )}
                    </TableCell>

                    {/* Circuit Info (electricity only) */}
                    {infrastructure.infrastructureType === infrastructureType.electricity && (
                      <>
                        <TableCell>
                          {circuitInfo?.name ?? <PriorityHighIcon color="error" />}
                        </TableCell>
                        <TableCell>
                          {(circuitInfo && stringifyPowerType(circuitInfo)) ?? (
                            <PriorityHighIcon color="error" />
                          )}
                        </TableCell>
                      </>
                    )}

                    {/* Actions */}
                    <TableCell>
                      <IconButton
                        size="small"
                        color="info"
                        disabled={isParentLoading || isChildLoading}
                        onClick={() => {
                          if (parentItem && childItem) {
                            setManageSubItemsInfrastructure({
                              parentItemId: infrastructure.parentItemId,
                              parentItemNumber: parentItem.itemNumber || '',
                              parentItemName: parentItem.name || '',
                              childItemId: infrastructure.childItemId,
                              childItemNumber: childItem.itemNumber || '',
                              childItemName: childItem.name || '',
                            });
                          }
                        }}
                        title={t('infrastructure.subItems.manageSubItems', 'Manage Sub-items')}
                      >
                        <ViewListIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Manage Sub-items Dialog */}
      {manageSubItemsInfrastructure && (
        <ManageSubItemsDialog
          open={!!manageSubItemsInfrastructure}
          onClose={() => {
            setManageSubItemsInfrastructure(null);
          }}
          parentItemId={manageSubItemsInfrastructure.parentItemId}
          parentItemNumber={manageSubItemsInfrastructure.parentItemNumber}
          parentItemName={manageSubItemsInfrastructure.parentItemName}
          childItemId={manageSubItemsInfrastructure.childItemId}
          childItemNumber={manageSubItemsInfrastructure.childItemNumber}
          childItemName={manageSubItemsInfrastructure.childItemName}
          infrastructureType={currentInfrastructureType}
        />
      )}
    </Box>
  );
};
